import { setValueValue } from '@databases/collab/y-database';
import { Database } from '@databases/model/database';
import { MutableEntry } from '@databases/model/entry';
import { FieldType } from '@databases/model/field';
import { AllValueJSON, MutableValue, UserType, Value } from '@databases/model/value';
import {
  CollabFilterType,
  MutableCollabFilter,
  MutableCollabFilterBooleanExpression,
} from '@databases/model/views/view-filter';
import {
  isNumericFieldType,
  nonNumericCollabCalculationTypes,
} from '@databases/quick-calc/collab-helpers';
import { DefaultRootState } from '@databases/redux';
import { DatabaseFieldType, DEFAULT_ENTRY_ID } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';

import {
  DatabaseChange,
  DatabaseChangeType,
  MultiValueChangeType,
  ValueChange,
} from '../../database/changesets';
import { convertType, getInitialValue } from '../../database/field-types';
import { getUniqueFieldName } from '../../database/utils';
import { getNewIndexWithHiddenFields } from '../../database/view/view';

import {
  backlinkDisplayTypeMap,
  calculationToCollabMapping,
  CardTypeToCollabMapping,
  CardWidthToCollabMapping,
  coverFieldIdToCollabMapping,
  dbFieldTypeMap,
  displayedDetailTypeMap,
  fieldToCollabJSON,
  filterToCollabJSON,
  filterUsageTypeToCollabMapping,
  numberFormatMap,
  numberSeparatorMap,
  parentPageTypeMapping,
  valueToCollabJSON,
  viewToCollabJSON,
} from './convert-to-collab';
import { fieldToDatabase, fieldTypeMap, valueToDatabase } from './convert-to-database';

const getTargetedFilter = (
  filter: MutableCollabFilter,
  path: number[],
  append?: boolean
): [MutableCollabFilter, number] => {
  let currentFilter = filter;
  let pathDepth = 0;
  for (pathDepth; pathDepth < (append ? path.length : path.length - 1); pathDepth++) {
    const filterIndex = path[pathDepth];
    if (
      filterIndex !== undefined &&
      (currentFilter.type === CollabFilterType.AND ||
        currentFilter.type === CollabFilterType.OR)
    ) {
      currentFilter = currentFilter.value[filterIndex];
    } else {
      break;
    }
  }
  return [currentFilter, pathDepth];
};

function applyDeltaValue(
  getMutableValue: <T extends FieldType>(type: T) => MutableValue<T>,
  change: ValueChange
) {
  switch (change.type) {
    case DatabaseFieldType.TEXT: {
      getMutableValue(FieldType.Text).applyDelta(change.delta);
      break;
    }
    case DatabaseFieldType.SELECT: {
      const value = getMutableValue(FieldType.Select);
      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add(change.delta.value);
            break;
          case MultiValueChangeType.EDIT:
            throw new Error('unsupported');
          case MultiValueChangeType.REMOVE:
            value.delete([value.value[change.delta.index]]);
            break;
          case MultiValueChangeType.REORDER:
            throw new Error('unsupported');
          case MultiValueChangeType.SET_TO:
            value.setTo(change.delta.value);
        }
      }
      break;
    }
    case DatabaseFieldType.ATTACHMENT: {
      const value = getMutableValue(FieldType.Attachment);
      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              fileId:
                'fileId' in change.delta.value ? change.delta.value.fileId || '' : '',
              id: change.delta.value._id,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].fileId =
              'fileId' in change.delta.value ? change.delta.value.fileId || '' : '';
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => {
                return {
                  id: v._id,
                  fileId: 'fileId' in v ? v.fileId || '' : '',
                };
              })
            );
        }
      }
      break;
    }
    case DatabaseFieldType.USER: {
      const value = getMutableValue(FieldType.User);
      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND: {
            const deltaValue = change.delta.value;
            value.add(
              typeof deltaValue === 'string'
                ? {
                    type: UserType.Current,
                  }
                : {
                    id: deltaValue._id,
                    ...('accountId' in deltaValue && { accountId: deltaValue.accountId }),
                    ...('displayName' in deltaValue && {
                      displayName: deltaValue.displayName,
                    }),
                    type: UserType.Referenced,
                  }
            );
            break;
          }
          case MultiValueChangeType.EDIT: {
            const deltaValue = change.delta.value;
            const user = value.value[change.delta.index];
            if (typeof deltaValue !== 'string') {
              if (user.type === UserType.Referenced) {
                user.accountId =
                  'accountId' in deltaValue ? deltaValue.accountId || '' : '';
                user.displayName =
                  'displayName' in deltaValue ? deltaValue.displayName || '' : '';
              } else {
                // need to change type
                value.delete(change.delta.index);
                value.insert(change.delta.index, {
                  id: deltaValue._id,
                  ...('accountId' in deltaValue && { accountId: deltaValue.accountId }),
                  ...('displayName' in deltaValue && {
                    displayName: deltaValue.displayName,
                  }),
                  type: UserType.Referenced,
                });
              }
            } else {
              if (user.type === UserType.Referenced) {
                // need to change type
                value.delete(change.delta.index);
                value.insert(change.delta.index, {
                  type: UserType.Current,
                });
              } else {
                // nothing to do
              }
            }
            break;
          }
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            if (typeof change.delta.value !== 'string') {
              value.setTo(
                change.delta.value.map((v) => ({
                  id: v._id,
                  ...('accountId' in v && { accountId: v.accountId }),
                  ...('displayName' in v && { displayName: v.displayName }),
                  type: UserType.Referenced,
                }))
              );
            } else {
              value.setTo([{ type: UserType.Current }]);
            }
        }
      }
      break;
    }
    case DatabaseFieldType.CHECKBOX: {
      getMutableValue(FieldType.Checkbox).value = change.value;
      break;
    }
    case DatabaseFieldType.PAGE_LINK: {
      const value = getMutableValue(FieldType.PageLink);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              contentId:
                'contentId' in change.delta.value
                  ? change.delta.value.contentId
                  : undefined,
              id: change.delta.value._id,
              ari: 'ari' in change.delta.value ? change.delta.value.ari : undefined,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].contentId =
              'contentId' in change.delta.value
                ? change.delta.value.contentId
                : undefined;
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => ({
                id: v._id,
                contentId: 'contentId' in v ? v.contentId || undefined : undefined,
                ari: 'ari' in v ? v.ari || undefined : undefined,
              }))
            );
        }
      }
      break;
    }
    case DatabaseFieldType.ENTRY_LINK: {
      const value = getMutableValue(FieldType.EntryLink);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              entryId:
                'entryId' in change.delta.value ? change.delta.value.entryId || '' : '',
              id: change.delta.value._id,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].entryId =
              'entryId' in change.delta.value ? change.delta.value.entryId || '' : '';
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => {
                return {
                  id: v._id,
                  entryId: 'entryId' in v ? v.entryId || '' : '',
                };
              })
            );
        }
      }
      break;
    }
    case DatabaseFieldType.HYPERLINK: {
      const value = getMutableValue(FieldType.Hyperlink);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              url: change.delta.value.url,
              id: change.delta.value._id,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].applyUrlDelta(change.delta.delta);
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => ({
                url: v.url,
                id: v._id,
              }))
            );
        }
      }
      break;
    }
    case DatabaseFieldType.JIRA_ISSUE: {
      const value = getMutableValue(FieldType.JiraIssue);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              key: 'key' in change.delta.value ? change.delta.value.key : '',
              id: change.delta.value._id,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].key =
              'key' in change.delta.value ? change.delta.value.key : '';
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => ({
                key: 'key' in v ? v.key : '',
                id: v._id,
              }))
            );
        }
      }
      break;
    }
    case DatabaseFieldType.NUMBER: {
      getMutableValue(FieldType.Number).value = change.value;
      break;
    }
    case DatabaseFieldType.DATE: {
      getMutableValue(FieldType.Date).value = change.value || ''; // FIXME: type can be undefined?
      break;
    }
  }
}

const getConvertedValue = (
  db: Database,
  fieldId: string,
  value: Value<FieldType> | undefined,
  type: FieldType,
  state: DefaultRootState | undefined
) => {
  const defaultValue = db.defaultEntry.getValue(fieldId);

  let reduxValue;
  if (value) {
    reduxValue = valueToDatabase(value.toJSON());
  } else if (defaultValue) {
    reduxValue = valueToDatabase(defaultValue.toJSON());
  } else {
    reduxValue = getInitialValue(fieldTypeMap[type]);
  }

  const convertedType = convertType(
    {
      field: fieldToDatabase(fieldId, db.fieldsById[fieldId].toJSON()),
      value: reduxValue,
    },
    fieldTypeMap[type],
    state
  );

  return valueToCollabJSON(convertedType, {});
};

export const addChangeToYjs = (
  ydb: Database,
  change: DatabaseChange,
  state: DefaultRootState | undefined
): void => {
  switch (change.type) {
    case DatabaseChangeType.SET_NAME: // not stored in yjs
    case DatabaseChangeType.RESET_NAME: // not stored in yjs
    case DatabaseChangeType.SET_SPACE: // not stored in yjs
    case DatabaseChangeType.SET_IS_GLOBAL: // not stored in yjs
    case DatabaseChangeType.UPDATE_FIELD: // unused
    case DatabaseChangeType.RESET_FIELD: // not possible
    case DatabaseChangeType.RESET_FIELD_NAME: // not possible
    case DatabaseChangeType.UNDELETE_FIELD: // not possible
    case DatabaseChangeType.RESET_VALUE: // not possible
    case DatabaseChangeType.UNDELETE_ENTRY: // not possible
      return;

    case DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.showDescription = change.showDescription;
      });
      break;
    case DatabaseChangeType.SET_MAIN_FIELD_ID:
      ydb.mutate((db) => {
        db.mainFieldId = change.mainFieldId;
      });
      break;
    case DatabaseChangeType.SET_IS_CREATABLE:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.creatable = change.isCreatable;
      });
      break;
    case DatabaseChangeType.SET_ALLOW_EDIT:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.allowEdit = change.allowEdit;
      });
      break;
    case DatabaseChangeType.SET_BACKLINK_DISPLAY_TYPE: {
      const convertedBacklinkDisplayType =
        backlinkDisplayTypeMap[change.backlinkDisplayType];
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.backlinkDisplayType = convertedBacklinkDisplayType;
      });
      break;
    }
    case DatabaseChangeType.SET_DATABASE_ID: {
      const destType = dbFieldTypeMap[DatabaseFieldType.ENTRY_LINK];

      const convertedValue = getConvertedValue(
        ydb,
        change.fieldId,
        ydb.defaultEntry.getValue(change.fieldId),
        destType,
        state
      );

      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.databaseId = change.contentId;
        applyDeltaValue(
          (destType) =>
            getOrSetValue(db.defaultEntry, change.fieldId, destType, convertedValue),
          {
            type: DatabaseFieldType.ENTRY_LINK,
            delta: { value: [], type: MultiValueChangeType.SET_TO },
          }
        );
      });
      break;
    }
    case DatabaseChangeType.SET_INCLUDE_VERSION_COMMENT:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.includeVersionComment = change.includeVersionComment;
      });
      break;
    case DatabaseChangeType.SET_IS_MULTI:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.multi = change.isMulti;
      });
      break;
    case DatabaseChangeType.SET_JIRA_FIELD:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.jiraField = change.jiraField;
      });
      break;
    case DatabaseChangeType.SET_JIRA_SERVER_ID:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.jiraServerId = change.jiraServerId;
      });
      break;
    case DatabaseChangeType.SET_CLOUD_ID:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.cloudId = change.cloudId;
      });
      break;
    case DatabaseChangeType.SET_NEWLINES:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.newlines = change.newlines;
      });
      break;
    case DatabaseChangeType.SET_RESOLVE:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.resolve = change.resolve;
      });
      break;
    case DatabaseChangeType.SET_DISPLAY_DETAIL: {
      const convertedDisplayedDetailType =
        displayedDetailTypeMap[change.displayedDetailTypes];
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.displayedDetail = convertedDisplayedDetailType;
      });
      break;
    }
    case DatabaseChangeType.SET_CONTENT_FIELD:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.contentField = change.contentField;
      });
      break;
    case DatabaseChangeType.SET_ENTRY_FIELD_FIELD:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.entryFieldField = change.entryFieldField;
      });
      break;
    case DatabaseChangeType.SET_PARENT_PAGE:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        if (change.newParentPage && change.newParentPage?.contentId) {
          field.parentPage = change.newParentPage?.contentId;
        } else {
          field.parentPage = undefined;
        }
      });
      break;
    case DatabaseChangeType.SET_PARENT_PAGE_OPTION:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.parentPageOption = change.parentPageOption;
      });
      break;
    case DatabaseChangeType.SET_IS_PARENT_PAGE_SOURCE_DATABASE:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.isParentPageSourceDatabase = change.isParentPageSourceDatabase;
      });
      break;
    case DatabaseChangeType.SET_PAGE_LOCATION:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];

        field.parentPageType = parentPageTypeMapping[change.newParentPageType];
        field.parentPageFieldId = change.newParentPageFieldId;
      });
      break;
    case DatabaseChangeType.SET_PAGE_TEMPLATE:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.pageTemplateId = change.newPageTemplate.templateId;
      });
      break;
    case DatabaseChangeType.SET_DISPLAY_ENTRY_ON_PAGE:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.displayEntryOnPage = change.displayEntryOnPage;
      });
      break;
    case DatabaseChangeType.SET_COVER_FIELD_ID:
      ydb.mutate((db) => {
        db.coverFieldId = change.coverFieldId;
      });
      break;
    case DatabaseChangeType.ADD_ENTRY:
      ydb.mutate((db) => {
        const entry =
          change.index != null
            ? db.addEntryAt(change.entry.id, change.index)
            : db.addEntry(change.entry.id);
        for (const fieldId of Object.keys(change.entry.values)) {
          const type = dbFieldTypeMap[change.entry.values[fieldId].type];
          if (!type) {
            throw new Error(
              'unsupported field type ' + change.entry.values[fieldId].type
            );
          }

          const value = entry.setValue(fieldId, type);
          const collabValue = valueToCollabJSON(change.entry.values[fieldId], {});
          if (collabValue) {
            setValueValue(value, collabValue);
          }
        }
      });
      break;
    case DatabaseChangeType.ADD_FIELD:
      ydb.mutate((db) => {
        if (change.index != null) {
          db.addFieldAt(
            change.field.id,
            fieldToCollabJSON(change.field, {}),
            change.index
          );
        } else {
          db.addField(change.field.id, fieldToCollabJSON(change.field, {}));
        }

        db.views.forEach((viewId: string) => {
          if (db.viewsById[viewId] && db.viewsById[viewId].fields) {
            if (viewId === change.currentViewId) {
              db.viewsById[viewId].setFieldVisibility(change.field.id, true, db.fields);
            } else {
              db.viewsById[viewId].setFieldVisibility(change.field.id, false, db.fields);
            }
          }
        });
      });
      break;
    case DatabaseChangeType.SET_FIELD_NAME:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        if (field) {
          field.applyNameDelta(change.delta);
        }
      });
      break;
    case DatabaseChangeType.SET_FIELD_DESCRIPTION:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        if (field) {
          field.applyDescriptionDelta(change.delta);
        }
      });
      break;
    case DatabaseChangeType.SET_FIELD_TYPE:
      ydb.mutate((db) => {
        const destType = dbFieldTypeMap[change.fieldType];
        if (!destType) {
          throw new Error('unsupported field type ' + change.fieldType);
        }
        db.fieldsById[change.fieldId].type = destType;
        db.views.forEach((viewId: string) => {
          const collabCalculationType = db.viewsById[viewId].calculations[change.fieldId];

          // If the destination type is numeric, we can keep the calculation.
          // This is because the non-numeric calculations are a subset of the
          // numeric calculations. If the destination type is non-numeric, we
          // need to check if the calculation is a non-numeric calculation. If
          // it isn't, we will remove it.
          if (
            collabCalculationType &&
            !isNumericFieldType(destType) &&
            !nonNumericCollabCalculationTypes.includes(collabCalculationType)
          ) {
            db.viewsById[viewId].removeCalculation(change.fieldId);
          }
        });
      });
      break;

    case DatabaseChangeType.SET_FIELD_WIDTH:
      ydb.mutate((db) => {
        db.fieldsById[change.fieldId].width = change.width;
      });
      break;
    case DatabaseChangeType.SET_FIELD_ID:
      ydb.mutate((db) => {
        db.fieldsById[change.fieldId].fieldId = change.settingsFieldId;
      });
      break;
    case DatabaseChangeType.SET_SELECT_OPTIONS:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        if (field) {
          switch (change.change.type) {
            case MultiValueChangeType.APPEND:
              field.selectOptions.add({
                id: change.change.value.id ?? change.change.value.label,
                label: change.change.value.label,
                color: change.change.value.color,
              });
              break;

            case MultiValueChangeType.REMOVE: {
              const option = field.selectOptions.options[change.change.index].id;
              field.selectOptions.delete(change.change.index);
              db.entries.forEach((entryId) => {
                const value = db.entriesById[entryId].getValue(change.fieldId);
                if (value && value.type === FieldType.Select) {
                  (value as MutableValue<FieldType.Select>).delete([option]);
                }
              });
              break;
            }

            case MultiValueChangeType.REORDER:
              field.selectOptions.reorder(
                change.change.currentIndex,
                change.change.newIndex
              );
              break;

            case 'set_color':
              if (field.selectOptions.options[change.change.index]) {
                field.selectOptions.options[change.change.index].color =
                  change.change.color;
              }
              break;

            case 'update_label':
              field.selectOptions.options[change.change.index]?.applyLabelDelta(
                change.change.delta
              );
              break;
          }
        }
      });
      break;
    case DatabaseChangeType.REORDER_FIELD:
      ydb.mutate((db) => {
        const newIndex = getNewIndexWithHiddenFields(
          change.newIndex,
          db.fields,
          change.hiddenFields
        );

        db.reorderField(change.fieldId, newIndex);
      });
      break;

    case DatabaseChangeType.CLONE_FIELDS:
      ydb.mutate((db) => {
        // Sort field IDs by their occurrence in the database and filter out non-existing ones
        const fieldIds = db.fields.filter((fieldId) =>
          Object.prototype.hasOwnProperty.call(change.fieldIds, fieldId)
        );

        const index = db.fields.indexOf(fieldIds[fieldIds.length - 1]) + 1;

        const fieldNames = db.fields.map((f) => db.fieldsById[f].name);
        const newFieldNames: string[] = [];

        for (let i = 0; i < fieldIds.length; i++) {
          const fieldId = fieldIds[i];
          const newFieldId = change.fieldIds[fieldId];
          const newFieldName = getUniqueFieldName(db.fieldsById[fieldId].name, [
            ...fieldNames,
            ...newFieldNames,
          ]);
          newFieldNames.push(newFieldName);

          db.addFieldAt(
            newFieldId,
            {
              ...db.fieldsById[fieldId].toJSON(),
              name: newFieldName,
            },
            index + i
          );

          db.views.forEach((viewId: string) => {
            if (db.viewsById[viewId] && db.viewsById[viewId].fields) {
              if (viewId === change.currentViewId) {
                db.viewsById[viewId].setFieldVisibility(newFieldId, true, db.fields);
              } else {
                db.viewsById[viewId].setFieldVisibility(newFieldId, false, db.fields);
              }
            }

            // copy quick calculations
            const calculation = db.viewsById[viewId].calculations[fieldId];
            if (calculation) {
              db.viewsById[viewId].setCalculation(newFieldId, calculation);
            }
          });

          for (const entry of [db.defaultEntry, ...Object.values(db.entriesById)]) {
            const value = entry?.getValue(fieldId);
            if (entry && value) {
              const newValue = entry.setValue(newFieldId, value.type);
              setValueValue(newValue, value.toJSON());
            }
          }
        }
      });
      break;

    case DatabaseChangeType.DELETE_FIELD:
      ydb.mutate((db) => {
        db.deleteField(change.fieldId);

        db.views.forEach((viewId: string) => {
          db.viewsById[viewId].removeFieldVisibilityField(change.fieldId, db.fields);
          db.viewsById[viewId].removeCalculation(change.fieldId);
        });

        Object.keys(db.entriesById).forEach((entryId) => {
          db.entriesById[entryId].removeValue(change.fieldId);
        });
      });
      break;

    case DatabaseChangeType.SET_FIELD_SETTINGS_NUMBER_FORMAT:
      ydb.mutate((db) => {
        db.fieldsById[change.fieldId].numberFormat =
          numberFormatMap[change.newValue.format];
      });
      break;

    case DatabaseChangeType.SET_FIELD_SETTINGS_MINIMUM_OR_MAXIMUM_FRACTION_DIGITS_FIELD:
      ydb.mutate((db) => {
        if (change.changeType === 'minimumFractionDigits') {
          db.fieldsById[change.fieldId].customNumberFormat.minFractionDigits =
            change.newValue;
        } else if (change.changeType === 'maximumFractionDigits') {
          db.fieldsById[change.fieldId].customNumberFormat.maxFractionDigits =
            change.newValue;
        }
      });
      break;

    case DatabaseChangeType.SET_FIELD_SETTINGS_SEPARATOR_FIELD:
      ydb.mutate((db) => {
        if (change.changeType === 'decimalSeparator') {
          if (change.newValue === undefined) {
            return;
          }
          db.fieldsById[change.fieldId].customNumberFormat.decimalSeparator =
            numberSeparatorMap[change.newValue];
        } else if (change.changeType === 'thousandsSeparator') {
          db.fieldsById[change.fieldId].customNumberFormat.thousandsSeparator =
            change.newValue ? numberSeparatorMap[change.newValue] : undefined;
        }
      });
      break;

    case DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD:
      ydb.mutate((db) => {
        if (change.changeType === 'prefix') {
          db.fieldsById[change.fieldId].customNumberFormat.prefix = change.newValue;
        } else if (change.changeType === 'suffix') {
          db.fieldsById[change.fieldId].customNumberFormat.suffix = change.newValue;
        }
      });
      break;

    case DatabaseChangeType.SET_VALUE: {
      const delta = change.change;
      if (!delta) {
        return;
      }
      const destType = dbFieldTypeMap[delta.type];
      if (!destType) {
        throw new Error('unsupported type ' + destType);
      }

      const convertedValue = getConvertedValue(
        ydb,
        change.fieldId,
        (change.entryId === DEFAULT_ENTRY_ID
          ? ydb.defaultEntry
          : ydb.entriesById[change.entryId]
        ).getValue(change.fieldId),
        destType,
        state
      );
      ydb.mutate((db) => {
        const entry =
          change.entryId === DEFAULT_ENTRY_ID
            ? db.defaultEntry
            : db.entriesById[change.entryId];
        const field = db.fieldsById[change.fieldId];
        if (!entry || !field) {
          return;
        }

        applyDeltaValue(
          (destType) => getOrSetValue(entry, change.fieldId, destType, convertedValue),
          delta
        );
      });
      break;
    }
    case DatabaseChangeType.BULK_SET_UNDEFINED_VALUES:
      ydb.mutate((db) => {
        const delta = change.change;
        if (!delta) {
          return;
        }
        const destType = dbFieldTypeMap[delta.type];
        if (!destType) {
          throw new Error('unsupported type ' + destType);
        }

        // reset all undefined to existing default value
        const valueJSON = db.defaultEntry.toJSON().values[change.fieldId];
        if (valueJSON) {
          for (const entryId of db.entries) {
            const entry = db.entriesById[entryId];
            if (entry && entry.getValue(change.fieldId) === undefined) {
              const value = entry.setValue(change.fieldId, valueJSON.type);
              setValueValue(value, valueJSON);
            }
          }
        } else {
          // set to empty value if no default has been set yet
          for (const entryId of db.entries) {
            const entry = db.entriesById[entryId];
            if (entry && entry.getValue(change.fieldId) === undefined) {
              const value = entry.setValue(change.fieldId, FieldType.Text);
              setValueValue(value, {
                type: FieldType.Text,
                value: '',
              });
            }
          }
        }

        // update default entry
        const convertedValue = getConvertedValue(
          ydb,
          change.fieldId,
          ydb.defaultEntry.getValue(change.fieldId),
          destType,
          state
        );

        applyDeltaValue(
          (destType) =>
            getOrSetValue(db.defaultEntry, change.fieldId, destType, convertedValue),
          delta
        );
      });
      break;

    case DatabaseChangeType.ADD_SAVED_VIEW:
      ydb.mutate((db) => {
        db.addView(change.view.id, viewToCollabJSON(change.view, {}));
      });
      break;

    case DatabaseChangeType.SET_SAVED_VIEW:
      ydb.mutate((db) => {
        db.setView(change.view.id, viewToCollabJSON(change.view, {}));
      });
      break;

    case DatabaseChangeType.SET_DEFAULT_SAVED_VIEW:
      ydb.mutate((db) => {
        db.defaultViewId = change.viewId;
      });
      break;

    case DatabaseChangeType.DELETE_SAVED_VIEW:
      ydb.mutate((db) => {
        db.deleteView(change.viewId);
      });
      break;

    case DatabaseChangeType.REORDER_SAVED_VIEW:
      ydb.mutate((db) => {
        db.reorderView(change.viewId, change.newIndex);
      });
      break;

    case DatabaseChangeType.UPDATE_SAVED_VIEW_NAME:
      ydb.mutate((db) => {
        const view = db.viewsById[change.viewId];
        if (view) {
          view.applyNameDelta(change.delta);
        }
      });
      break;

    case DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES: {
      ydb.mutate((db) => {
        change.changes.forEach((viewChange) => {
          switch (viewChange.type) {
            case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.layoutType = CardTypeToCollabMapping[viewChange.newLayoutType];
              }
              break;
            }

            case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.cardWidth = CardWidthToCollabMapping[viewChange.newCardWidth];
              }
              break;
            }

            case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.coverField = coverFieldIdToCollabMapping(viewChange.newCoverFieldId);
              }
              break;
            }

            case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.groupFieldId = viewChange.newGroupFieldId;
              }
              break;
            }

            case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.dateFieldId = viewChange.newDateFieldId;
              }
              break;
            }

            case DatabaseChangeType.UPDATE_SAVED_VIEW_NAME: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.applyNameDelta(viewChange.delta);
              }
              break;
            }

            case DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.setFieldVisibility(
                  viewChange.fieldKey,
                  viewChange.fieldValue,
                  db.fields
                );
              }
              break;
            }

            case DatabaseChangeType.UPDATE_SAVED_VIEW_SORT: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                view.sort = viewChange.sort;
              }
              break;
            }

            case DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                if (
                  view.filter &&
                  (view.filter.type === CollabFilterType.AND ||
                    view.filter.type === CollabFilterType.OR)
                ) {
                  view.filter.type = filterUsageTypeToCollabMapping(
                    viewChange.newFilterUsageType
                  );
                }
              }
              break;
            }

            case DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                if (!view.filter || viewChange.path === undefined) {
                  view.setFilter({
                    type: CollabFilterType.AND,
                    value: [],
                  });
                } else {
                  const [currentFilter, pathDepth] = getTargetedFilter(
                    view.filter,
                    viewChange.path
                  );

                  if (
                    currentFilter.type === CollabFilterType.AND ||
                    currentFilter.type === CollabFilterType.OR
                  ) {
                    const targetFilter = currentFilter.value[viewChange.path[pathDepth]];
                    if (
                      viewChange.change &&
                      targetFilter &&
                      (targetFilter.type === CollabFilterType.EQUALS ||
                        targetFilter.type === CollabFilterType.GREATER ||
                        targetFilter.type === CollabFilterType.LESS)
                    ) {
                      applyDeltaValue(
                        (type) => targetFilter.setValue(type),
                        viewChange.change
                      );
                    } else if (
                      targetFilter &&
                      targetFilter.type === CollabFilterType.CONTAINS
                    ) {
                      if (typeof viewChange.newFilterValue === 'string') {
                        targetFilter.value = viewChange.newFilterValue;
                      }
                    }
                  }
                }
              }
              break;
            }

            case DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID:
            case DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                if (!viewChange.newFilter) {
                  view.clearFilter();
                } else if (view.filter && viewChange.path) {
                  const [currentFilter, pathDepth] = getTargetedFilter(
                    view.filter,
                    viewChange.path
                  );

                  if (
                    currentFilter.type === CollabFilterType.AND ||
                    currentFilter.type === CollabFilterType.OR
                  ) {
                    const collabFilter = filterToCollabJSON(viewChange.newFilter, {});
                    if (collabFilter) {
                      currentFilter.setFilter(viewChange.path[pathDepth], collabFilter);
                    } else {
                      currentFilter.remove(viewChange.path[pathDepth]);

                      if (currentFilter.value.length === 0 && pathDepth === 0) {
                        view.clearFilter();
                      }
                    }
                  }
                }
              }
              break;
            }

            case DatabaseChangeType.APPEND_SAVED_VIEW_FILTER: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                if (!view.filter) {
                  const filter = view.setFilter({
                    type: CollabFilterType.AND,
                    value: [],
                  }) as MutableCollabFilterBooleanExpression;

                  if (viewChange.newFilter.type === DatabaseFilterType.EQUALS) {
                    filter.add({
                      type: CollabFilterType.EQUALS,
                      fieldId: '',
                      value: {
                        type: FieldType.Text,
                        value: viewChange.newFilter.value.value as string,
                      },
                    });
                  }
                } else {
                  const currentFilter = viewChange.path
                    ? getTargetedFilter(view.filter, viewChange.path)[0]
                    : view.filter;

                  if (
                    currentFilter.type === CollabFilterType.AND ||
                    currentFilter.type === CollabFilterType.OR
                  ) {
                    const collabFilter = filterToCollabJSON(viewChange.newFilter, {});
                    if (collabFilter) {
                      currentFilter.add(collabFilter);
                    }
                  }
                }
              }
              break;
            }

            case DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER: {
              const view = db.viewsById[viewChange.viewId];
              if (view) {
                if (!view.filter || !viewChange.path) {
                  view.clearFilter();
                } else {
                  const [currentFilter, pathDepth] = getTargetedFilter(
                    view.filter,
                    viewChange.path
                  );

                  if (
                    currentFilter.type === CollabFilterType.AND ||
                    currentFilter.type === CollabFilterType.OR
                  ) {
                    currentFilter.remove(viewChange.path[pathDepth]);

                    if (currentFilter.value.length === 0 && pathDepth === 0) {
                      view.clearFilter();
                    }
                  }
                }
              }
              break;
            }

            case DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS:
              ydb.mutate((db) => {
                const view = db.viewsById[viewChange.viewId];
                if (view) {
                  view.setCalculation(
                    viewChange.fieldId,
                    calculationToCollabMapping[viewChange.value]
                  );
                }
              });
              break;

            case DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION:
              ydb.mutate((db) => {
                const view = db.viewsById[viewChange.viewId];
                if (view) {
                  view.removeCalculation(viewChange.fieldId);
                }
              });
              break;
            case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES:
              ydb.mutate((db) => {
                const view = db.viewsById[viewChange.viewId];
                if (view) {
                  view.showNumberedEntries = viewChange.newShowNumberedEntries;
                }
              });
          }
        });
      });
      return;
    }

    case DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS:
      ydb.mutate((db) => {
        const view = db.viewsById[change.viewId];
        if (view) {
          view.setCalculation(change.fieldId, calculationToCollabMapping[change.value]);
        }
      });
      break;

    case DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION:
      ydb.mutate((db) => {
        const view = db.viewsById[change.viewId];
        if (view) {
          view.removeCalculation(change.fieldId);
        }
      });
      break;

    case DatabaseChangeType.DELETE_ENTRY:
      ydb.mutate((db) => {
        db.deleteEntry(change.entryId);
      });
      break;

    case DatabaseChangeType.TOGGLE_LOCK_STRUCTURE:
      ydb.mutate((db) => {
        db.isLocked = change.newValue;
      });
      break;

    case DatabaseChangeType.REORDER_ENTRIES:
      ydb.mutate((db) => {
        db.reorderEntries(change.entryIds, change.newIndex);
      });
      break;

    case DatabaseChangeType.CLONE_ENTRIES:
      ydb.mutate((db) => {
        // Sort entry IDs by their occurrence in the database and filter out non-existing ones
        const entryIds = db.entries.filter((entryId) =>
          Object.prototype.hasOwnProperty.call(change.entryIds, entryId)
        );

        const index = db.entries.indexOf(entryIds[entryIds.length - 1]) + 1;

        for (let i = 0; i < entryIds.length; i++) {
          const entryId = entryIds[i];
          const newEntry = db.addEntryAt(change.entryIds[entryId], index + i);

          const entryJSON = db.entriesById[entryId].toJSON();
          for (const [fieldId, valueJSON] of Object.entries(entryJSON.values)) {
            const newValue = newEntry.setValue(fieldId, valueJSON.type);
            setValueValue(newValue, valueJSON);
          }
        }
      });
      break;

    case DatabaseChangeType.CLEAR_ENTRIES:
      ydb.mutate((db) => {
        for (const entryId of change.entryIds || db.entries) {
          for (const fieldId of change.fieldIds || db.fields) {
            const existingFieldType = db.fieldsById[fieldId].type;
            const entry = db.entriesById[entryId];
            entry.removeValue(fieldId);

            // set an empty value for the field
            entry.setValue(fieldId, existingFieldType);
          }
        }
      });
      break;

    case DatabaseChangeType.SET_DEFAULT_ENABLED:
      ydb.mutate((db) => {
        const field = db.fieldsById[change.fieldId];
        field.defaultEnabled = change.defaultEnabled;
      });
      break;

    default:
      // const impossible: never = change;
      // throw new Error(`unknown change type: ${change.type}`);
      // eslint-disable-next-line no-console
      console.error('unknown change type', change.type, change);
  }
};

function getOrSetValue<T extends FieldType>(
  entry: MutableEntry,
  fieldId: string,
  type: T,
  defaultValue?: AllValueJSON
): MutableValue<T> {
  const value = entry.getValue(fieldId);
  if (value && value.type === type) {
    return value as MutableValue<T>;
  } else {
    const newValue = entry.setValue(fieldId, type);
    if (defaultValue) {
      setValueValue(newValue, defaultValue);
    }

    return newValue;
  }
}
